import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Warning, ArrowRight, RocketLaunch, Spinner } from "@phosphor-icons/react";

export default function LaunchReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/tools/launch-readiness")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="tinylabel">Settings · Health check</div>
          <h1 className="text-3xl font-black flex items-center gap-2">
            <RocketLaunch size={30} weight="bold" className="text-teal-600" />
            Items that need attention
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            A one-tap scan for missing data that will cause problems — workers without logins, projects without a supervisor email, clients without a billing address, and so on. Green = all good. Red = fix soon. Amber = recommended.
          </p>
        </div>
        <button onClick={load} data-testid="rerun-launch-check"
                className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">
          {loading ? "Scanning…" : "Re-run scan"}
        </button>
      </div>

      {!data && loading && (
        <div className="p-12 text-center text-slate-400">
          <Spinner size={40} className="mx-auto animate-spin" />
          <div className="mt-3">Scanning…</div>
        </div>
      )}

      {data && (
        <>
          <div className={`p-5 rounded-lg border-2 mb-6 ${data.ready_to_launch ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}
               data-testid="launch-verdict">
            {data.ready_to_launch ? (
              <div className="flex items-center gap-3">
                <CheckCircle size={40} weight="fill" className="text-emerald-600" />
                <div>
                  <div className="text-2xl font-black text-emerald-900">Nothing needs attention</div>
                  <div className="text-sm text-emerald-800">
                    All required checks pass{data.warning_count > 0 ? ` (${data.warning_count} recommendation${data.warning_count === 1 ? "" : "s"} below)` : ""}.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <XCircle size={40} weight="fill" className="text-rose-600" />
                <div>
                  <div className="text-2xl font-black text-rose-900">
                    {data.blocking_count} item{data.blocking_count === 1 ? "" : "s"} need{data.blocking_count === 1 ? "s" : ""} attention
                  </div>
                  <div className="text-sm text-rose-800">Address each red row below, then re-run the scan.</div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {data.checks.map((c) => (
              <CheckRow key={c.key} check={c} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CheckRow({ check }) {
  const [showMissing, setShowMissing] = useState(false);
  const iconCls = check.ok ? "text-emerald-600" : check.warning_only ? "text-amber-600" : "text-rose-600";
  const bgCls = check.ok
    ? "bg-white border-slate-200"
    : check.warning_only
      ? "bg-amber-50 border-amber-200"
      : "bg-rose-50 border-rose-200";
  return (
    <div className={`border rounded-md p-4 ${bgCls}`} data-testid={`check-${check.key}`}>
      <div className="flex items-start gap-3">
        {check.ok ? <CheckCircle size={24} weight="fill" className={iconCls} />
                    : check.warning_only ? <Warning size={24} weight="fill" className={iconCls} />
                    : <XCircle size={24} weight="fill" className={iconCls} />}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900">{check.label}</div>
          {check.count_total !== undefined && (
            <div className="text-xs text-slate-500 mt-0.5">
              {check.count_ok} of {check.count_total} complete
            </div>
          )}
          {!check.ok && (
            <>
              <div className="text-sm text-slate-700 mt-2">{check.fix_hint}</div>
              {check.missing && check.missing.length > 0 && (
                <div className="mt-2">
                  <button onClick={() => setShowMissing(!showMissing)}
                          className="text-xs font-semibold text-teal-700 hover:underline"
                          data-testid={`toggle-missing-${check.key}`}>
                    {showMissing ? "Hide" : "Show"} {check.missing.length} missing
                  </button>
                  {showMissing && (
                    <ul className="mt-2 space-y-1 text-sm text-slate-700 bg-white/60 rounded p-3">
                      {check.missing.slice(0, 20).map((m, i) => (
                        <li key={i} className="font-mono text-xs">
                          • {m.name}{m.email ? ` (${m.email})` : ""}
                        </li>
                      ))}
                      {check.missing.length > 20 && (
                        <li className="text-xs text-slate-500">…and {check.missing.length - 20} more</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {!check.ok && check.fix_route && (
          <Link to={check.fix_route}
                className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 hover:bg-white flex items-center gap-1"
                data-testid={`fix-${check.key}`}>
            Fix this <ArrowRight size={12} weight="bold" />
          </Link>
        )}
      </div>
    </div>
  );
}
